import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Megaphone, DollarSign, Plus, Trash2, Check, X, Inbox, Send, ExternalLink, Building2, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function Sponsorships() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("marketplace"); // marketplace | listings | inbound | bookings
  const [me, setMe] = useState(null);
  const [brands, setBrands] = useState([]);
  const [listings, setListings] = useState([]);
  const [marketplace, setMarketplace] = useState([]);
  const [inbound, setInbound] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setMe(await api.billingMe());
    setBrands(await api.listBrands());
    setListings(await api.myListings());
    setMarketplace(await api.browseMarketplace().catch(() => []));
    setInbound(await api.inboundBookings());
    setBookings(await api.myBookings());
  };

  useEffect(() => { load(); }, []);

  const tier = me?.tier;
  const tierOk = me?.is_creator || tier === "mogul" || tier === "founding";

  return (
    <div className="max-w-6xl space-y-8" data-testid="sponsorships-page">
      <div>
        <div className="text-eyebrow mb-2 flex items-center gap-2"><Megaphone size={12}/> Sponsorships</div>
        <h1 className="font-display font-black tracking-tighter text-5xl">Get paid to mention. Or buy a slot.</h1>
        <p className="text-sm text-[#52525B] mt-3 max-w-2xl">Open your newsletter to paid sponsors. Or book a placement in someone else's. Vitality keeps 20%, you keep 80%. Auto-paid via Stripe Connect.</p>
      </div>

      {/* TABS */}
      <div className="flex gap-2 border-b border-black">
        {[
          { id: "marketplace", label: "Browse sponsors", icon: Building2 },
          { id: "listings", label: "My listings (host)", icon: Megaphone, gated: !tierOk },
          { id: "inbound", label: `Inbound (${inbound.length})`, icon: Inbox, gated: !tierOk },
          { id: "bookings", label: "My bookings (buyer)", icon: Send },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 ${tab === t.id ? "border-black font-bold" : "border-transparent text-[#52525B] hover:text-black"}`}
          >
            <t.icon size={13}/> {t.label} {t.gated && <Lock size={10} className="text-[#A1A1AA]"/>}
          </button>
        ))}
      </div>

      {tab === "marketplace" && (
        <Marketplace listings={marketplace} onReload={load}/>
      )}

      {tab === "listings" && !tierOk && (
        <PaywallCard navigate={navigate}/>
      )}

      {tab === "listings" && tierOk && (
        <MyListings
          brands={brands}
          listings={listings}
          editing={editing}
          setEditing={setEditing}
          onReload={load}
        />
      )}

      {tab === "inbound" && !tierOk && <PaywallCard navigate={navigate}/>}

      {tab === "inbound" && tierOk && (
        <Inbound bookings={inbound} onReload={load}/>
      )}

      {tab === "bookings" && (
        <MyBookings bookings={bookings}/>
      )}
    </div>
  );
}

function PaywallCard({ navigate }) {
  return (
    <div className="border-2 border-black p-6 bg-white">
      <div className="flex items-center gap-2 mb-2"><Lock size={16}/><div className="text-eyebrow">Mogul tier required</div></div>
      <h3 className="font-display font-black tracking-tighter text-3xl mb-2">Open your newsletter to sponsors on Mogul tier.</h3>
      <p className="text-sm text-[#52525B] mb-4">$399/mo. Set your price, accept what you want, keep 80% per slot. Built-in Stripe Connect handles the payouts.</p>
      <button onClick={() => navigate("/pricing")} className="bg-black text-white px-5 py-3 text-sm hover:bg-[#002FA7]">See plans →</button>
    </div>
  );
}

function Marketplace({ listings, onReload }) {
  if (!listings.length) return <div className="border border-dashed border-[#E2E4E9] p-8 text-center text-sm text-[#52525B]">No sponsor listings yet. Be the first to open yours, or check back later.</div>;
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="marketplace-grid">
      {listings.map((l) => (
        <MarketplaceCard key={l.listing_id} listing={l} onBooked={onReload}/>
      ))}
    </div>
  );
}

function MarketplaceCard({ listing, onBooked }) {
  const [show, setShow] = useState(false);
  const [pitch, setPitch] = useState("");
  const [target, setTarget] = useState("");
  const [working, setWorking] = useState(false);

  const book = async () => {
    if (!pitch || !target) { toast.error("Pitch + target URL required"); return; }
    setWorking(true);
    try {
      const r = await api.bookSponsorship({ listing_id: listing.listing_id, pitch, target_url: target });
      if (r.checkout_url) window.location.href = r.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally { setWorking(false); }
  };

  return (
    <div className="border-2 border-black bg-white p-5" data-testid={`listing-${listing.listing_id}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="font-display font-bold text-xl tracking-tight">{listing.brand_name}</div>
          <div className="text-xs mono text-[#52525B] mt-1">{listing.audience_size?.toLocaleString() || "?"} subs · {listing.open_rate_pct || 0}% open rate</div>
        </div>
        <div className="font-display font-black text-2xl tracking-tighter">${(listing.price_cents / 100).toFixed(0)}</div>
      </div>
      {listing.description && <p className="text-sm text-[#52525B] mt-2 leading-relaxed line-clamp-3">{listing.description}</p>}
      {!show ? (
        <button onClick={() => setShow(true)} className="mt-4 bg-black text-white px-4 py-2 text-sm hover:bg-[#002FA7]" data-testid={`book-${listing.listing_id}`}>
          Book this slot →
        </button>
      ) : (
        <div className="mt-4 space-y-2 border-t border-[#E2E4E9] pt-3">
          <textarea value={pitch} onChange={(e) => setPitch(e.target.value)} placeholder="Your pitch — what do you want the host to feature?" rows={3} className="w-full border border-black p-2 text-sm"/>
          <input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="https://your-link.com" className="w-full border border-black p-2 text-sm mono"/>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShow(false)} className="text-eyebrow px-3 py-1.5 hover:text-black">Cancel</button>
            <button onClick={book} disabled={working} className="bg-black text-white px-4 py-2 text-sm hover:bg-[#002FA7] disabled:opacity-50">
              {working ? "…" : `Pay $${(listing.price_cents / 100).toFixed(0)} →`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MyListings({ brands, listings, editing, setEditing, onReload }) {
  const empty = !editing && listings.length === 0;
  return (
    <div className="space-y-4">
      {!editing && (
        <button onClick={() => setEditing({ brand_id: "", price_cents: 5000, audience_size: 0, open_rate_pct: 0, description: "", accepts_links: true, max_per_month: 2 })}
                className="bg-black text-white px-5 py-3 text-sm flex items-center gap-2 hover:bg-[#002FA7]" data-testid="new-listing-button">
          <Plus size={14}/> New listing
        </button>
      )}
      {editing && <ListingForm brands={brands} listing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onReload(); }}/>}
      {empty && <div className="border border-dashed border-[#E2E4E9] p-8 text-center text-sm text-[#52525B]">No listings yet. Open one to start receiving paid sponsorships.</div>}
      {!editing && listings.length > 0 && (
        <div className="grid md:grid-cols-2 gap-3">
          {listings.map((l) => (
            <div key={l.listing_id} className="border-2 border-black p-4 bg-white" data-testid={`my-listing-${l.listing_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display font-bold text-lg">{l.brand_name}</div>
                  <div className="text-xs mono text-[#52525B] mt-1">${(l.price_cents / 100).toFixed(0)} · {l.status}</div>
                </div>
                <button onClick={async () => { await api.deactivateListing(l.listing_id); onReload(); }} className="text-eyebrow text-[#A1A1AA] hover:text-[#E11D48]" data-testid={`deactivate-${l.listing_id}`}>
                  <Trash2 size={14}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ListingForm({ brands, listing, onClose, onSaved }) {
  const [form, setForm] = useState(listing);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.brand_id) { toast.error("Pick a brand"); return; }
    if (form.price_cents < 500) { toast.error("Minimum $5"); return; }
    setBusy(true);
    try {
      await api.saveSponsorshipListing(form);
      toast.success("Listing saved.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-black p-6 bg-white space-y-3" data-testid="listing-form">
      <div className="text-eyebrow">New listing</div>
      <div className="grid md:grid-cols-2 gap-3">
        <select value={form.brand_id} onChange={(e) => setForm({ ...form, brand_id: e.target.value })} className="border-2 border-black p-2 text-sm" data-testid="listing-brand-select">
          <option value="">Pick brand…</option>
          {brands.map((b) => <option key={b.brand_id} value={b.brand_id}>{b.name}</option>)}
        </select>
        <div className="flex items-center border-2 border-black">
          <span className="px-3 mono text-sm">$</span>
          <input type="number" value={form.price_cents / 100} onChange={(e) => setForm({ ...form, price_cents: Math.round(parseFloat(e.target.value || "0") * 100) })} placeholder="50" className="flex-1 p-2 text-sm mono outline-none" data-testid="listing-price-input"/>
          <span className="px-3 mono text-xs text-[#52525B] border-l border-black">per slot</span>
        </div>
        <input type="number" value={form.audience_size} onChange={(e) => setForm({ ...form, audience_size: parseInt(e.target.value || "0", 10) })} placeholder="Subscribers (e.g. 5000)" className="border-2 border-black p-2 text-sm" data-testid="listing-audience-input"/>
        <input type="number" value={form.open_rate_pct} onChange={(e) => setForm({ ...form, open_rate_pct: parseInt(e.target.value || "0", 10) })} placeholder="Open rate % (e.g. 42)" className="border-2 border-black p-2 text-sm" data-testid="listing-open-rate-input"/>
      </div>
      <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe what kind of sponsorships you accept. Tone, topics, anti-topics." rows={3} className="w-full border-2 border-black p-2 text-sm" data-testid="listing-description-input"/>
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="text-eyebrow px-3 py-1.5 hover:text-black">Cancel</button>
        <button onClick={save} disabled={busy} className="bg-black text-white px-5 py-2 text-sm hover:bg-[#002FA7] disabled:opacity-50" data-testid="save-listing-button">
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function Inbound({ bookings, onReload }) {
  if (!bookings.length) return <div className="border border-dashed border-[#E2E4E9] p-8 text-center text-sm text-[#52525B]">No inbound sponsorship requests yet.</div>;
  return (
    <div className="space-y-3" data-testid="inbound-list">
      {bookings.map((b) => (
        <div key={b.booking_id} className="border-2 border-black p-4 bg-white" data-testid={`inbound-${b.booking_id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-display font-bold">${(b.amount_cents / 100).toFixed(0)} · {b.status}</div>
              <div className="text-xs text-[#52525B] mt-1">Your net: ${(b.host_net_cents / 100).toFixed(2)} · Vitality fee: ${(b.platform_fee_cents / 100).toFixed(2)}</div>
            </div>
            {b.status === "awaiting_host_approval" && (
              <div className="flex gap-2">
                <button onClick={async () => { await api.decideSponsorship(b.booking_id, "reject"); onReload(); }} className="border border-[#E11D48] text-[#E11D48] px-3 py-1.5 text-sm hover:bg-[#E11D48] hover:text-white" data-testid={`reject-${b.booking_id}`}>
                  <X size={12} className="inline mr-1"/> Reject (refund)
                </button>
                <button onClick={async () => { await api.decideSponsorship(b.booking_id, "approve"); onReload(); toast.success("Approved. Add the mention to your next scheduled post."); }} className="bg-black text-white px-3 py-1.5 text-sm hover:bg-[#059669]" data-testid={`approve-${b.booking_id}`}>
                  <Check size={12} className="inline mr-1"/> Approve
                </button>
              </div>
            )}
          </div>
          <div className="mt-3 text-sm leading-relaxed">{b.pitch}</div>
          {b.target_url && <a href={b.target_url} target="_blank" rel="noreferrer" className="text-xs mono text-[#002FA7] hover:underline mt-2 inline-flex items-center gap-1">{b.target_url} <ExternalLink size={10}/></a>}
        </div>
      ))}
    </div>
  );
}

function MyBookings({ bookings }) {
  if (!bookings.length) return <div className="border border-dashed border-[#E2E4E9] p-8 text-center text-sm text-[#52525B]">You haven't booked any sponsorships yet.</div>;
  return (
    <div className="space-y-2" data-testid="my-bookings-list">
      {bookings.map((b) => (
        <div key={b.booking_id} className="border border-[#E2E4E9] p-3 flex items-center justify-between text-sm" data-testid={`booking-${b.booking_id}`}>
          <div>
            <div className="font-semibold">${(b.amount_cents / 100).toFixed(0)} · {b.status}</div>
            <div className="text-xs text-[#52525B] mt-1">{b.created_at?.slice(0, 10)}</div>
          </div>
          {b.target_url && <a href={b.target_url} target="_blank" rel="noreferrer" className="text-eyebrow hover:text-black"><ExternalLink size={12}/></a>}
        </div>
      ))}
    </div>
  );
}
